import { useMemo } from "react";
import { TableIcon } from "lucide-react";
import { AttributeCodeVisibilityEnum, AttributeKindEnum } from "@brewbase/db/types";
import { trpc, type RouterOutputs } from "@/hooks/trpc";
import { Combobox, type ComboboxOption } from "@/components/ui/combobox";

/**
 * A searchable picker over the results catalogue (the public analyte registry).
 *
 * Public codes only: a private code is a template's own formula name and is
 * never something to order from a lab or map a report onto. Grouped by
 * category, showing the primary unit; a profile table is marked as such.
 */

export type Analyte = RouterOutputs["tests"]["attributeCodes"]["list"][number];

export function usePublicAnalytes() {
  return trpc.tests.attributeCodes.list.useQuery({
    visibility: AttributeCodeVisibilityEnum.PUBLIC,
  });
}

export function analyteLabel(a: Analyte): string {
  const unit = a.units[0];
  return `${a.name}${unit && unit !== "1" ? ` · ${unit}` : ""}`;
}

export function analyteOptions(
  analytes: Analyte[] | undefined,
  filter?: (a: Analyte) => boolean,
): ComboboxOption[] {
  return (analytes ?? [])
    .filter((a) => (filter ? filter(a) : true))
    .map((a) => ({
      value: a.code,
      label: (
        <span className="flex items-center gap-1.5">
          {a.kind === AttributeKindEnum.TABULAR_DATA && (
            <TableIcon className="size-3.5 shrink-0 text-olive-9" />
          )}
          <span>{a.name}</span>
          <span className="font-mono text-xs text-olive-9">{a.code}</span>
          {a.units[0] && a.units[0] !== "1" && (
            <span className="text-xs text-olive-9">{a.units[0]}</span>
          )}
        </span>
      ),
      labelString: `${a.name} ${a.code} ${a.units.join(" ")}`,
      group: a.category ?? "Uncategorised",
    }));
}

export function AnalytePicker({
  value,
  onChange,
  placeholder,
  filter,
  className,
  disabled,
}: {
  value: string | null;
  onChange: (code: string | null) => void;
  placeholder?: string;
  filter?: (a: Analyte) => boolean;
  className?: string;
  disabled?: boolean;
}) {
  const { data: analytes } = usePublicAnalytes();
  const options = useMemo(() => analyteOptions(analytes, filter), [analytes, filter]);

  return (
    <Combobox
      value={value ?? ""}
      onValueChange={(v) => onChange(v || null)}
      options={options}
      placeholder={placeholder ?? "Result…"}
      searchPlaceholder="Search the results catalogue…"
      triggerClassName={className ?? "h-7 text-xs"}
      disabled={disabled}
    />
  );
}
